const BRAND = {
    name: 'SohozKaj',
    url: 'sohozkaj.com',
    primary: '#ea6b0e',
    primaryDark: '#c2560a',
    ink: '#1c1815',
    muted: '#6b635c',
    line: '#ece8e4',
    tint: '#fff4e6',
    surface: '#ffffff',
    page: '#ffffff',
};

/** A4 printable area at 96dpi with 12mm margins on each side. */
export const EXPORT_PAGE_WIDTH_PX = Math.round(((210 - 24) / 25.4) * 96);
export const EXPORT_PAGE_HEIGHT_PX = Math.round(((297 - 24) / 25.4) * 96);

export function getEducationBoardResultLogoUrl(origin, language = 'bn') {
    const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
    const file = language === 'en' ? 'logo-en.svg' : 'logo.svg';
    return `${base}/images/${file}`;
}

const LOGO_VIEWBOX = { width: 512, height: 124.95 };
const logoDataUrlCache = new Map();

function rasterizeSvgMarkupToPng(svgText, renderWidth = 340) {
    return new Promise((resolve, reject) => {
        const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();

        img.onload = () => {
            const aspect = LOGO_VIEWBOX.width / LOGO_VIEWBOX.height;
            const canvas = document.createElement('canvas');
            canvas.width = renderWidth;
            canvas.height = Math.round(renderWidth / aspect);
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Canvas unavailable'));
                return;
            }
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            URL.revokeObjectURL(objectUrl);
            resolve(canvas.toDataURL('image/png'));
        };

        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Logo rasterize failed'));
        };

        img.src = objectUrl;
    });
}

/** Rasterized PNG data URL for html2canvas-safe PDF export. */
export async function getEducationBoardResultLogoDataUrl(origin, language = 'bn') {
    const cacheKey = `${origin}|${language}`;
    if (logoDataUrlCache.has(cacheKey)) {
        return logoDataUrlCache.get(cacheKey);
    }

    const url = getEducationBoardResultLogoUrl(origin, language);
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Logo fetch failed');
    }

    const svgText = await response.text();
    const dataUrl = await rasterizeSvgMarkupToPng(svgText);
    logoDataUrlCache.set(cacheKey, dataUrl);
    return dataUrl;
}

function escapeHtml(value) {
    if (value === null || value === undefined) return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function buildGradeRows(grades) {
    return grades.map((grade, index) => `
        <tr class="${index % 2 ? 'sk-row-alt' : ''}">
            <td class="sk-code">${escapeHtml(grade.code || '—')}</td>
            <td>${escapeHtml(grade.subject || '—')}</td>
            <td class="sk-grade">${escapeHtml(grade.grade || '—')}</td>
        </tr>
    `).join('');
}

function buildGradesTable(labels, grades, { showTitle = true } = {}) {
    if (!grades.length) return '';
    return `
        <section class="sk-section sk-grades-section">
            ${showTitle ? `<h2 class="sk-section-title">${escapeHtml(labels.subjectGrades)}</h2>` : ''}
            <table class="sk-grades">
                <thead>
                    <tr>
                        <th class="sk-th-code">${escapeHtml(labels.gradeTable.code)}</th>
                        <th>${escapeHtml(labels.gradeTable.subject)}</th>
                        <th class="sk-th-grade">${escapeHtml(labels.gradeTable.grade)}</th>
                    </tr>
                </thead>
                <tbody>${buildGradeRows(grades)}</tbody>
            </table>
        </section>
    `;
}

function buildInfoCards(infoItems, labels) {
    return infoItems
        .filter(([, value]) => value)
        .map(([key, value]) => `
            <div class="sk-info">
                <span class="sk-info-label">${escapeHtml(labels.fields[key] || key)}</span>
                <span class="sk-info-value">${escapeHtml(value)}</span>
            </div>
        `)
        .join('');
}

/**
 * @param {{
 *   result: Record<string, any>,
 *   infoItems: Array<[string, any]>,
 *   labels: Record<string, string>,
 *   logoUrl?: string,
 *   page?: {
 *     showHeader?: boolean,
 *     showHero?: boolean,
 *     showDetails?: boolean,
 *     gradeRows?: Array<Record<string, any>>,
 *     showGradesTitle?: boolean,
 *     showFooter?: boolean,
 *   },
 * }} params
 */
export function buildEducationBoardResultBody({
    result,
    infoItems,
    labels,
    logoUrl,
    page = {},
}) {
    const {
        showHeader = true,
        showHero = true,
        showDetails = true,
        gradeRows = Array.isArray(result?.grades) ? result.grades : [],
        showGradesTitle = true,
        showFooter = true,
    } = page;

    const resolvedLogoUrl = logoUrl || '/images/logo.svg';
    const gpaBadge = result?.gpa && showHero
        ? `<span class="sk-gpa">${escapeHtml(labels.fields.gpa)}: ${escapeHtml(result.gpa)}</span>`
        : '';

    return `
        <div class="sk-doc">
            ${showHeader ? `
            <header class="sk-header">
                <div class="sk-brand">
                    <img
                        src="${escapeHtml(resolvedLogoUrl)}"
                        alt="${escapeHtml(BRAND.name)}"
                        class="sk-logo-img"
                        crossorigin="anonymous"
                    />
                </div>
                <div class="sk-header-meta">
                    <div class="sk-doc-title">${escapeHtml(labels.title)}</div>
                    <div class="sk-doc-tagline">${escapeHtml(labels.documentTagline)}</div>
                </div>
            </header>
            ` : ''}

            ${showHero ? `
            <section class="sk-hero">
                <div class="sk-hero-label">${escapeHtml(labels.resultStatus)}</div>
                <div class="sk-hero-status">${escapeHtml(result?.result || labels.published)}</div>
                ${gpaBadge}
            </section>
            ` : ''}

            ${showDetails ? `
            <section class="sk-section sk-details-section">
                <h2 class="sk-section-title">${escapeHtml(labels.studentDetails)}</h2>
                <div class="sk-info-grid">${buildInfoCards(infoItems, labels)}</div>
            </section>
            ` : ''}

            ${buildGradesTable(labels, gradeRows, { showTitle: showGradesTitle })}

            ${showFooter ? `
            <footer class="sk-footer">
                <p class="sk-footer-source">${escapeHtml(labels.officialSource)}</p>
                <p class="sk-footer-disclaimer">${escapeHtml(labels.documentDisclaimer)}</p>
            </footer>
            ` : ''}
        </div>
    `;
}

export function educationBoardResultStyles() {
    return `
        @page {
            size: A4 portrait;
            margin: 12mm;
        }
        *{box-sizing:border-box;margin:0;padding:0;}
        html,body{background:${BRAND.page};}
        .sk-export-page{
            width:${EXPORT_PAGE_WIDTH_PX}px;
            height:${EXPORT_PAGE_HEIGHT_PX}px;
            background:${BRAND.page};
            overflow:hidden;
        }
        .sk-doc{
            font-family:'Hind Siliguri','Noto Sans Bengali','Segoe UI',system-ui,-apple-system,sans-serif;
            color:${BRAND.ink};
            background:${BRAND.page};
            width:${EXPORT_PAGE_WIDTH_PX}px;
            max-width:${EXPORT_PAGE_WIDTH_PX}px;
            margin:0 auto;
            padding:0;
        }
        .sk-header{
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:16px;
            padding-bottom:16px;
            border-bottom:3px solid ${BRAND.primary};
            break-inside:avoid;
            page-break-inside:avoid;
        }
        .sk-brand{display:flex;flex-direction:column;align-items:flex-start;}
        .sk-logo-img{
            width:170px;
            height:auto;
            max-height:42px;
            object-fit:contain;
            object-position:left center;
            display:block;
            flex:none;
        }
        .sk-header-meta{text-align:right;}
        .sk-doc-title{font-size:15px;font-weight:700;color:${BRAND.ink};}
        .sk-doc-tagline{font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:0.08em;color:${BRAND.muted};margin-top:2px;}

        .sk-hero{
            margin-top:18px;
            text-align:center;
            padding:20px 16px;
            border:1px solid #ffd9ad;
            background:linear-gradient(180deg,#fff8ef,${BRAND.tint});
            border-radius:14px;
            break-inside:avoid;
            page-break-inside:avoid;
        }
        .sk-hero-label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.12em;color:${BRAND.muted};}
        .sk-hero-status{font-size:24px;font-weight:900;color:${BRAND.primaryDark};margin-top:6px;}
        .sk-gpa{
            display:inline-block;margin-top:10px;
            padding:5px 14px;border-radius:999px;
            background:#e3f6ec;color:#1e9e5e;font-size:12px;font-weight:800;
        }

        .sk-section{margin-top:18px;}
        .sk-details-section{
            break-inside:avoid;
            page-break-inside:avoid;
        }
        .sk-section-title{
            font-size:12px;font-weight:800;color:${BRAND.ink};
            margin-bottom:10px;padding-left:10px;
            border-left:4px solid ${BRAND.primary};
            text-transform:uppercase;letter-spacing:0.04em;
            break-after:avoid;
            page-break-after:avoid;
        }
        .sk-info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
        .sk-info{
            border:1px solid ${BRAND.line};
            background:${BRAND.surface};
            border-radius:10px;
            padding:10px 12px;
            break-inside:avoid;
            page-break-inside:avoid;
        }
        .sk-info-label{
            display:block;font-size:9px;font-weight:700;text-transform:uppercase;
            letter-spacing:0.06em;color:#8c847d;
        }
        .sk-info-value{display:block;margin-top:3px;font-size:13px;font-weight:700;color:${BRAND.ink};word-break:break-word;}

        .sk-grades{width:100%;border-collapse:collapse;border:1px solid ${BRAND.line};}
        .sk-grades thead{display:table-header-group;}
        .sk-grades th{
            background:${BRAND.tint};color:${BRAND.primaryDark};
            font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:0.04em;
            padding:10px 12px;text-align:left;
        }
        .sk-th-grade{text-align:center;}
        .sk-grades td{padding:9px 12px;font-size:12px;border-top:1px solid ${BRAND.line};background:${BRAND.surface};}
        .sk-grades tr{break-inside:avoid;page-break-inside:avoid;}
        .sk-row-alt td{background:#faf9f8;}
        .sk-code{font-weight:700;}
        .sk-grade{text-align:center;font-weight:800;color:${BRAND.primary};}

        .sk-footer{
            margin-top:22px;padding-top:12px;border-top:1px solid ${BRAND.line};
            text-align:center;
            break-inside:avoid;
            page-break-inside:avoid;
        }
        .sk-footer-source{
            font-size:10px;color:${BRAND.muted};font-weight:600;
        }
        .sk-footer-disclaimer{
            margin-top:6px;font-size:9px;line-height:1.55;color:#a8a099;
        }

        @media print{
            html,body{background:#fff;}
            .sk-doc{width:100%;max-width:100%;padding:0;background:#fff;}
        }
    `;
}

/**
 * Split grades across pages using measured heights (matches browser print pagination).
 * @param {Array<Record<string, any>>} grades
 * @param {{
 *   pageHeight: number,
 *   fixedTopHeight: number,
 *   footerHeight: number,
 *   gradesTitleHeight: number,
 *   theadHeight: number,
 *   rowHeight: number,
 * }} metrics
 */
export function splitGradesAcrossPages(grades, metrics) {
    const {
        pageHeight,
        fixedTopHeight,
        footerHeight,
        gradesTitleHeight,
        theadHeight,
        rowHeight,
    } = metrics;

    if (!grades.length) {
        return [{
            showHeader: true,
            showHero: true,
            showDetails: true,
            gradeRows: [],
            showGradesTitle: false,
            showFooter: true,
        }];
    }

    const safeRowHeight = Math.max(rowHeight, 1);
    const tableHeadBlock = gradesTitleHeight + theadHeight;
    const singlePageCapacity = Math.floor(
        (pageHeight - fixedTopHeight - tableHeadBlock - footerHeight) / safeRowHeight,
    );

    if (grades.length <= singlePageCapacity) {
        return [{
            showHeader: true,
            showHero: true,
            showDetails: true,
            gradeRows: grades,
            showGradesTitle: true,
            showFooter: true,
        }];
    }

    const pages = [];
    const firstPageCapacity = pageHeight - fixedTopHeight - tableHeadBlock;
    let firstCount = Math.max(1, Math.floor(firstPageCapacity / safeRowHeight));
    firstCount = Math.min(firstCount, grades.length - 1);

    pages.push({
        showHeader: true,
        showHero: true,
        showDetails: true,
        gradeRows: grades.slice(0, firstCount),
        showGradesTitle: true,
        showFooter: false,
    });

    let rowStart = firstCount;
    while (rowStart < grades.length) {
        const remaining = grades.length - rowStart;
        const fitsOnLastPage = remaining <= Math.floor(
            (pageHeight - theadHeight - footerHeight) / safeRowHeight,
        );
        const capacity = pageHeight - theadHeight - (fitsOnLastPage ? footerHeight : 0);
        let count = Math.max(1, Math.floor(capacity / safeRowHeight));
        count = Math.min(count, remaining);

        pages.push({
            showHeader: false,
            showHero: false,
            showDetails: false,
            gradeRows: grades.slice(rowStart, rowStart + count),
            showGradesTitle: false,
            showFooter: rowStart + count >= grades.length,
        });

        rowStart += count;
    }

    return pages;
}

export function buildEducationBoardResultPrintHtml(params) {
    const { labels, logoUrl } = params;
    const resolvedLogoUrl = logoUrl || '/images/logo.svg';
    return `<!DOCTYPE html><html lang="en"><head>
        <meta charset="utf-8" />
        <title>${escapeHtml(labels.title)}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&display=swap" rel="stylesheet" />
        <style>${educationBoardResultStyles()}
            body{background:#fff;padding:0;margin:0;}
        </style>
    </head><body>${buildEducationBoardResultBody({ ...params, logoUrl: resolvedLogoUrl })}</body></html>`;
}

export function buildEducationBoardResultPdfPageHtml(params, pageConfig) {
    const { labels, logoUrl } = params;
    const resolvedLogoUrl = logoUrl || '/images/logo.svg';
    return `<!DOCTYPE html><html lang="en"><head>
        <meta charset="utf-8" />
        <title>${escapeHtml(labels.title)}</title>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;600;700&display=swap" rel="stylesheet" />
        <style>${educationBoardResultStyles()}
            body{background:#fff;padding:0;margin:0;}
        </style>
    </head><body>
        <div class="sk-export-page">
            ${buildEducationBoardResultBody({ ...params, logoUrl: resolvedLogoUrl, page: pageConfig })}
        </div>
    </body></html>`;
}
